import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { Col, Row, Tooltip, Popconfirm, Spin, Dropdown, Space, notification } from "antd";
import Lottie from "lottie-react";
import { ThemeFilterBtn, ThemeButton, ThemeModal } from "../../Styles/Theme";
import { ThemeQstName } from "../SurveyComponents/QstFormat/QstStyle";
import Squares from "../../Dashboard/Squares";
import { EditOutlined, DeleteOutlined, PlusOutlined, SyncOutlined, ReloadOutlined, HolderOutlined, EnterOutlined, ExportOutlined, ImportOutlined, CloseOutlined, UploadOutlined, FileImageOutlined, VideoCameraOutlined, FilePdfOutlined, FilePdfFilled, LoadingOutlined, PictureOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { normalizeQuestions, downloadAsExcel, downloadAsText, parseImportFile } from "../../../utils/questionExportImport";
import { validateMediaFile, getMediaType, formatFileSize } from "../../../utils/fileValidation";
import { uploadSingleFile, generateDownloadUrl } from "../../../services/fileUploadService";
import ChatPanel from "./ChatPanel";
import { normalizeDemographics } from "./TargetAudienceForm";
import questionGenAnimation from "../../../assets/lottie/question-gen.json";
import {
  RightPreviewCard,
  RightCardBody,
  RightScrollArea,
  QuestionCardHeaderWrapper,
  QuestionCardHeader,
  QuestionBadge,
  QuestionHeaderActions,
  QuestionEditWrapper,
  QuestionsContainer,
  NoQuestionsState,
  NoQuestionsTitle,
  QuestionRegenerateBtn,
  DraggableQuestionCard,
  QuestionCardInner,
  DragHandle,
  InsertButtonWrapper,
  InsertAfterButton,
  InsertModalOverlay,
  InsertModalContent,
  InsertModalTitle,
  InsertModalInput,
  InsertModalActions,
  InsertModalButton,
  SaveButtonWrapper,
  PopconfirmGlobalStyles,
  FollowupContainer,
  FollowupRow,
  FollowupBadge,
  FollowupText,
  FollowupActions,
  FollowupActionBtn,
  AddFollowupBtn,
  FollowupDragHandle,
  InsertModalLabel,
  MediaUploadArea,
  MediaPreviewCard,
  MediaPreviewThumb,
  MediaPreviewInfo,
  MediaRemoveBtn,
  MediaBadge,
  MediaDisplayWrapper,
  PdfDisplayCard,
} from "./QuestionPreview.style";

const AUDIENCE_FIELDS_TO_TRACK = ['country', 'language', 'state', 'dma', 'ageRange', 'gender', 'race', 'topic'];

const areArraysEqual = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return a === b;
  if (a.length !== b.length) return false;
  return a.every((val, idx) => val === b[idx]);
};

const hasAudienceChanged = (original, current) => {
  if (!original || !current) return false;

  for (const field of AUDIENCE_FIELDS_TO_TRACK) {
    if (field === 'ageRange') {
      const origMin = original.ageRange?.min;
      const origMax = original.ageRange?.max;
      const currMin = current.ageRange?.min;
      const currMax = current.ageRange?.max;
      if (origMin !== currMin || origMax !== currMax) return true;
    } else if (Array.isArray(original[field]) || Array.isArray(current[field])) {
      if (!areArraysEqual(original[field] || [], current[field] || [])) return true;
    } else {
      if ((original[field] || '') !== (current[field] || '')) return true;
    }
  }
  return false;
};

const QuestionPreview = ({
  surveyTitle,
  messages,
  questions,
  demographics,
  onSendMessage,
  isSending,
  onAddQuestion,
  onUpdateQuestion,
  onDeleteQuestion,
  onSaveDemographics,
  isSavingDemographics,
  onSaveSurvey,
  onSaveAndBuildAIPoll,
  isSavingSurvey,
  onRegenerateAllQuestions,
  onSaveAndRegenerateQuestions,
  onRegenerateQuestion,
  isRegenerating,
  regeneratingQuestionId,
  onReorderQuestions,
  onInsertQuestion,
  onSurveyTitleChange,
  isLoading = false,
  canExport = false,
  canImport = false,
  onImportQuestions,
}) => {
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [baselineDemographics, setBaselineDemographics] = useState(null);
  const [audienceChanged, setAudienceChanged] = useState(false);
  const [pendingDemographics, setPendingDemographics] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [localQuestions, setLocalQuestions] = useState([]);
  const [insertModalOpen, setInsertModalOpen] = useState(false);
  const [insertAfterIndex, setInsertAfterIndex] = useState(null);
  const [insertQuestionText, setInsertQuestionText] = useState("");
  // Follow-up states
  const [editingFollowup, setEditingFollowup] = useState(null); // { questionId, followupIndex }
  const [editingFollowupValue, setEditingFollowupValue] = useState("");
  const [draggedFollowup, setDraggedFollowup] = useState(null); // { questionId, index }
  const [followupModalOpen, setFollowupModalOpen] = useState(false);
  const [followupModalQuestionId, setFollowupModalQuestionId] = useState(null);
  const [followupInsertAfterIndex, setFollowupInsertAfterIndex] = useState(null); // null = append, number = insert after
  const [newFollowupText, setNewFollowupText] = useState("");

  // Media upload state
  const [insertMediaData, setInsertMediaData] = useState(null); // { media_type, url, filename, size, mimetype, file_id, file_key }
  const [insertMediaUploading, setInsertMediaUploading] = useState(false);
  const [followupMediaData, setFollowupMediaData] = useState(null);
  const [followupMediaUploading, setFollowupMediaUploading] = useState(false);
  const mediaInputRef = useRef(null);
  const followupMediaInputRef = useRef(null);
  // Existing question/followup media attach
  const existingMediaInputRef = useRef(null);
  const [existingMediaTarget, setExistingMediaTarget] = useState(null); // { questionId, followupIndex? }
  const [existingMediaUploading, setExistingMediaUploading] = useState(null); // questionId or "qId-fIdx"
  const [previewMedia, setPreviewMedia] = useState(null); // { media_type, url, filename }

  // Export/Import state
  const importFileRef = useRef(null);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [pendingImport, setPendingImport] = useState(null);
  const [isImporting, setIsImporting] = useState(false);

  const sortedQuestions = useMemo(() => {
    return [...(questions || [])].sort((a, b) => a.order_index - b.order_index);
  }, [questions]);

  useEffect(() => {
    setLocalQuestions(sortedQuestions);
  }, [sortedQuestions]);

  const normalizedDemographics = useMemo(() => normalizeDemographics(demographics), [demographics]);


  useEffect(() => {
    if (sortedQuestions.length > 0 && !baselineDemographics && normalizedDemographics) {
      setBaselineDemographics(JSON.parse(JSON.stringify(normalizedDemographics)));
    }
  }, [sortedQuestions.length, normalizedDemographics, baselineDemographics]);

  useEffect(() => {
    if (baselineDemographics && pendingDemographics && sortedQuestions.length > 0) {
      const changed = hasAudienceChanged(baselineDemographics, pendingDemographics);
      setAudienceChanged(changed);
    }
  }, [baselineDemographics, pendingDemographics, sortedQuestions.length]);

  const handleDemographicsChange = useCallback(
    (values) => {
      setPendingDemographics(values);
    },
    []
  );

  const handleSaveDemographics = useCallback(() => {
    const demographicsToSave = pendingDemographics || normalizedDemographics;
    if (onSaveDemographics) {
      onSaveDemographics(demographicsToSave);
      setBaselineDemographics(JSON.parse(JSON.stringify(demographicsToSave)));
      setAudienceChanged(false);
    }
  }, [onSaveDemographics, pendingDemographics, normalizedDemographics]);

  const handleSaveAndRegenerate = useCallback(() => {
    const demographicsToSave = pendingDemographics || normalizedDemographics;
    if (onSaveAndRegenerateQuestions) {
      onSaveAndRegenerateQuestions(demographicsToSave);
      setBaselineDemographics(JSON.parse(JSON.stringify(demographicsToSave)));
      setAudienceChanged(false);
    }
  }, [onSaveAndRegenerateQuestions, pendingDemographics, normalizedDemographics]);

  const handleRegenerateQuestion = useCallback((questionId) => {
    if (onRegenerateQuestion) {
      onRegenerateQuestion(questionId);
    }
  }, [onRegenerateQuestion]);

  const handleEditQuestion = (question) => {
    setEditingQuestionId(question._id);
    setEditingValue(question.question_text);
  };

  const handleSaveQuestion = () => {
    if (!editingValue.trim()) return;
    onUpdateQuestion?.(editingQuestionId, editingValue);
    setEditingQuestionId(null);
    setEditingValue("");
  };

  const handleCancelEdit = () => {
    setEditingQuestionId(null);
    setEditingValue("");
  };

  const handleDragStart = useCallback((e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const updated = [...localQuestions];
    const draggedItem = updated[draggedIndex];
    updated.splice(draggedIndex, 1);
    updated.splice(index, 0, draggedItem);

    setLocalQuestions(updated);
    setDraggedIndex(index);
  }, [draggedIndex, localQuestions]);

  const handleDragEnd = useCallback(() => {
    if (draggedIndex === null) return;

    const reorderedIds = localQuestions.map(q => q._id);
    onReorderQuestions?.(reorderedIds);
    setDraggedIndex(null);
  }, [draggedIndex, localQuestions, onReorderQuestions]);

  const handleInsertAfter = useCallback((e, index) => {
    e.stopPropagation();
    e.preventDefault();
    setInsertAfterIndex(index);
    setInsertQuestionText("");
    setInsertModalOpen(true);
  }, []);

  const handleInsertModalClose = useCallback(() => {
    setInsertModalOpen(false);
    setInsertAfterIndex(null);
    setInsertQuestionText("");
    setInsertMediaData(null);
  }, []);

  const handleInsertModalSubmit = useCallback(() => {
    if (!insertQuestionText.trim()) return;
    onInsertQuestion?.(insertAfterIndex, insertQuestionText.trim(), insertMediaData);
    handleInsertModalClose();
  }, [insertQuestionText, insertAfterIndex, onInsertQuestion, handleInsertModalClose, insertMediaData]);

  // Follow-up handlers
  const handleAddFollowup = useCallback((questionId, insertAfterIndex = null) => {
    setFollowupModalQuestionId(questionId);
    setFollowupInsertAfterIndex(insertAfterIndex);
    setNewFollowupText("");
    setFollowupModalOpen(true);
  }, []);

  const handleFollowupModalClose = useCallback(() => {
    setFollowupModalOpen(false);
    setFollowupModalQuestionId(null);
    setFollowupInsertAfterIndex(null);
    setNewFollowupText("");
    setFollowupMediaData(null);
  }, []);

  const handleFollowupModalSubmit = useCallback(() => {
    if (!newFollowupText.trim() || !followupModalQuestionId) return;
    const question = localQuestions.find(q => q._id === followupModalQuestionId);
    if (!question) return;
    const existingFollowups = [...(question.followups || [])];

    const newFollowup = { text: newFollowupText.trim(), order_index: 0 };
    if (followupMediaData) newFollowup.media = followupMediaData;

    let newFollowups;
    if (followupInsertAfterIndex !== null) {
      existingFollowups.splice(followupInsertAfterIndex + 1, 0, newFollowup);
      newFollowups = existingFollowups.map((f, idx) => ({ ...f, order_index: idx }));
    } else {
      newFollowup.order_index = existingFollowups.length;
      newFollowups = [...existingFollowups, newFollowup];
    }

    onUpdateQuestion?.(followupModalQuestionId, question.question_text, newFollowups);
    handleFollowupModalClose();
  }, [newFollowupText, followupModalQuestionId, followupInsertAfterIndex, localQuestions, onUpdateQuestion, handleFollowupModalClose, followupMediaData]);

  const handleEditFollowup = useCallback((questionId, followupIndex, text) => {
    setEditingFollowup({ questionId, followupIndex });
    setEditingFollowupValue(text);
  }, []);

  const handleSaveFollowup = useCallback(() => {
    if (!editingFollowup || !editingFollowupValue.trim()) return;
    const { questionId, followupIndex } = editingFollowup;
    const question = localQuestions.find(q => q._id === questionId);
    if (!question) return;
    const updatedFollowups = (question.followups || []).map((f, idx) =>
      idx === followupIndex ? { ...f, text: editingFollowupValue.trim() } : f
    );
    onUpdateQuestion?.(questionId, question.question_text, updatedFollowups);
    setEditingFollowup(null);
    setEditingFollowupValue("");
  }, [editingFollowup, editingFollowupValue, localQuestions, onUpdateQuestion]);

  const handleCancelFollowupEdit = useCallback(() => {
    setEditingFollowup(null);
    setEditingFollowupValue("");
  }, []);

  const handleDeleteFollowup = useCallback((questionId, followupIndex) => {
    const question = localQuestions.find(q => q._id === questionId);
    if (!question) return;
    const updatedFollowups = (question.followups || []).filter((_, idx) => idx !== followupIndex);
    onUpdateQuestion?.(questionId, question.question_text, updatedFollowups);
  }, [localQuestions, onUpdateQuestion]);

  const handleFollowupDragStart = useCallback((e, questionId, index) => {
    setDraggedFollowup({ questionId, index });
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleFollowupDragOver = useCallback((e, questionId, targetIndex) => {
    e.preventDefault();
    if (!draggedFollowup || draggedFollowup.questionId !== questionId || draggedFollowup.index === targetIndex) return;
    const updated = localQuestions.map(q => {
      if (q._id === questionId) {
        const followups = [...(q.followups || [])];
        const item = followups[draggedFollowup.index];
        followups.splice(draggedFollowup.index, 1);
        followups.splice(targetIndex, 0, item);
        return { ...q, followups };
      }
      return q;
    });
    setLocalQuestions(updated);
    setDraggedFollowup({ questionId, index: targetIndex });
  }, [draggedFollowup, localQuestions]);

  const handleFollowupDragEnd = useCallback(() => {
    if (!draggedFollowup) return;
    const question = localQuestions.find(q => q._id === draggedFollowup.questionId);
    if (question) {
      onUpdateQuestion?.(question._id, question.question_text, question.followups);
    }
    setDraggedFollowup(null);
  }, [draggedFollowup, localQuestions, onUpdateQuestion]);

  // Export/Import handlers
  const handleExportQuestions = useCallback((format) => {
    if (!sortedQuestions.length) {
      notification.warning({ message: 'No questions to export' });
      return;
    }
    const normalized = normalizeQuestions(sortedQuestions, 'survey');
    const name = surveyTitle || 'survey';
    if (format === 'excel') downloadAsExcel(normalized, name);
    else downloadAsText(normalized, name);
  }, [sortedQuestions, surveyTitle]);

  const exportMenuItems = [
    { key: 'excel', label: 'Excel (.xls)', onClick: () => handleExportQuestions('excel') },
    { key: 'text', label: 'Text (.txt)', onClick: () => handleExportQuestions('text') },
  ];

  const handleImportFileChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const { questions: parsed, error } = await parseImportFile(file);
    if (error) {
      notification.error({ message: error });
      return;
    }
    setPendingImport({ questions: parsed, fileName: file.name });
    setImportModalVisible(true);
  }, []);

  const handleImportConfirm = useCallback(async () => {
    if (!pendingImport || !onImportQuestions) return;
    setIsImporting(true);
    try {
      await onImportQuestions(pendingImport.questions);
      setImportModalVisible(false);
      setPendingImport(null);
    } catch {
      // Error notification already shown by parent
    } finally {
      setIsImporting(false);
    }
  }, [pendingImport, onImportQuestions]);

  // Media upload handlers
  const handleMediaFileSelect = useCallback(async (file, target) => {
    const validation = validateMediaFile(file);
    if (!validation.valid) {
      notification.error({ message: 'Invalid File', description: validation.error });
      return;
    }

    const setUploading = target === 'followup' ? setFollowupMediaUploading : setInsertMediaUploading;
    const setData = target === 'followup' ? setFollowupMediaData : setInsertMediaData;

    setUploading(true);
    try {
      const result = await uploadSingleFile(file, 'survey-question-media', null);
      const downloadRes = await generateDownloadUrl(result.fileId);
      const mediaType = getMediaType(file.name);
      setData({
        media_type: mediaType,
        url: downloadRes?.data?.downloadUrl || '',
        filename: file.name,
        size: file.size,
        mimetype: file.type,
        file_id: result.fileId,
        file_key: result.fileKey,
      });
    } catch (err) {
      notification.error({ message: 'Upload Failed', description: err.message || 'Could not upload file' });
    } finally {
      setUploading(false);
    }
  }, []);

  const handleMediaInputChange = useCallback((e, target) => {
    const file = e.target.files?.[0];
    if (file) handleMediaFileSelect(file, target);
    e.target.value = '';
  }, [handleMediaFileSelect]);

  const handleMediaDrop = useCallback((e, target) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleMediaFileSelect(file, target);
  }, [handleMediaFileSelect]);

  const handleMediaDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Attach media to existing question or followup
  const handleAttachMediaToExisting = useCallback((questionId, followupIndex) => {
    setExistingMediaTarget({ questionId, followupIndex });
    setTimeout(() => existingMediaInputRef.current?.click(), 0);
  }, []);

  const handleExistingMediaInputChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !existingMediaTarget) return;

    const validation = validateMediaFile(file);
    if (!validation.valid) {
      notification.error({ message: 'Invalid File', description: validation.error });
      setExistingMediaTarget(null);
      return;
    }

    const { questionId, followupIndex } = existingMediaTarget;
    const uploadKey = followupIndex !== undefined ? `${questionId}-${followupIndex}` : questionId;
    setExistingMediaUploading(uploadKey);

    try {
      const result = await uploadSingleFile(file, 'survey-question-media', null);
      const downloadRes = await generateDownloadUrl(result.fileId);
      const mediaType = getMediaType(file.name);
      const mediaData = {
        media_type: mediaType,
        url: downloadRes?.data?.downloadUrl || '',
        filename: file.name,
        size: file.size,
        mimetype: file.type,
        file_id: result.fileId,
        file_key: result.fileKey,
      };

      const question = localQuestions.find(q => q._id === questionId);
      if (!question) return;

      if (followupIndex !== undefined) {
        // Attach to followup
        const updatedFollowups = (question.followups || []).map((f, idx) =>
          idx === followupIndex ? { ...f, media: mediaData } : f
        );
        onUpdateQuestion?.(questionId, question.question_text, updatedFollowups);
      } else {
        // Attach to question
        onUpdateQuestion?.(questionId, question.question_text, undefined, mediaData);
      }
    } catch (err) {
      notification.error({ message: 'Upload Failed', description: err.message || 'Could not upload file' });
    } finally {
      setExistingMediaUploading(null);
      setExistingMediaTarget(null);
    }
  }, [existingMediaTarget, localQuestions, onUpdateQuestion]);

  // Remove media from existing question or followup
  const handleRemoveMedia = useCallback((questionId, followupIndex) => {
    const question = localQuestions.find(q => q._id === questionId);
    if (!question) return;

    if (followupIndex !== undefined) {
      const updatedFollowups = (question.followups || []).map((f, idx) =>
        idx === followupIndex ? { ...f, media: null } : f
      );
      onUpdateQuestion?.(questionId, question.question_text, updatedFollowups);
    } else {
      onUpdateQuestion?.(questionId, question.question_text, undefined, null);
    }
  }, [localQuestions, onUpdateQuestion]);

  const getMediaBadgeIcon = (type) => {
    if (type === 'image') return <PictureOutlined />;
    if (type === 'video') return <VideoCameraOutlined />;
    if (type === 'pdf') return <FilePdfOutlined />;
    return null;
  };

  const getMediaBadgeLabel = (type) => {
    if (type === 'image') return 'Image';
    if (type === 'video') return 'Video';
    if (type === 'pdf') return 'PDF';
    return '';
  };

  // Render media upload area for modals
  const renderMediaUpload = (mediaData, uploading, target) => {
    if (uploading) {
      return (
        <MediaUploadArea>
          <LoadingOutlined className="upload-icon" spin />
          <span className="upload-text">Uploading...</span>
        </MediaUploadArea>
      );
    }

    if (mediaData) {
      return (
        <MediaPreviewCard>
          <MediaPreviewThumb>
            {mediaData.media_type === 'image' ? (
              <img src={mediaData.url} alt={mediaData.filename} />
            ) : mediaData.media_type === 'video' ? (
              <PlayCircleOutlined className="media-icon" />
            ) : (
              <FilePdfOutlined className="media-icon" />
            )}
          </MediaPreviewThumb>
          <MediaPreviewInfo>
            <div className="media-filename">{mediaData.filename}</div>
            <div className="media-meta">{formatFileSize(mediaData.size)} &middot; {getMediaBadgeLabel(mediaData.media_type).toUpperCase()}</div>
          </MediaPreviewInfo>
          <MediaRemoveBtn onClick={() => target === 'followup' ? setFollowupMediaData(null) : setInsertMediaData(null)}>
            <CloseOutlined />
          </MediaRemoveBtn>
        </MediaPreviewCard>
      );
    }

    return (
      <MediaUploadArea
        onClick={() => target === 'followup' ? followupMediaInputRef.current?.click() : mediaInputRef.current?.click()}
        onDrop={(e) => handleMediaDrop(e, target)}
        onDragOver={handleMediaDragOver}
      >
        <UploadOutlined className="upload-icon" />
        <span className="upload-text">Click to upload or drag and drop</span>
        <span className="upload-hint">Image (PNG, JPG), Video (MP4), or PDF</span>
      </MediaUploadArea>
    );
  };

  // Render media display in question/followup cards
  const renderMediaDisplay = (media, questionId, followupIndex) => {
    if (!media || !media.media_type) return null;

    const removeOverlay = questionId ? (
      <Popconfirm
        title="Remove Media"
        description="Remove the attached media?"
        onConfirm={() => handleRemoveMedia(questionId, followupIndex)}
        okText="Yes"
        cancelText="No"
        placement="topRight"
      >
        <MediaRemoveBtn className="media-remove-overlay">
          <CloseOutlined />
        </MediaRemoveBtn>
      </Popconfirm>
    ) : null;

    if (media.media_type === 'image') {
      return (
        <MediaDisplayWrapper>
          {removeOverlay}
          <div className="media-container media-image" onClick={() => setPreviewMedia(media)}>
            <img src={media.url} alt={media.filename || 'Question media'} />
          </div>
        </MediaDisplayWrapper>
      );
    }

    if (media.media_type === 'video') {
      return (
        <MediaDisplayWrapper>
          {removeOverlay}
          <div className="media-container media-video">
            <video controls controlsList="nodownload noplaybackrate" disablePictureInPicture preload="metadata" src={media.url} />
          </div>
        </MediaDisplayWrapper>
      );
    }

    if (media.media_type === 'pdf') {
      return (
        <PdfDisplayCard>
          <div className="pdf-icon">
            <FilePdfFilled style={{ fontSize: 32, color: '#e74c3c' }} />
          </div>
          <div className="pdf-info">
            <div className="pdf-title">PDF Document</div>
            <div className="pdf-subtitle">{media.filename || 'Document'}</div>
          </div>
          <div className="pdf-actions">
            <button className="pdf-view-btn" onClick={() => setPreviewMedia(media)}>View</button>
            {questionId && (
              <Popconfirm
                title="Remove Media"
                description="Remove the attached media?"
                onConfirm={() => handleRemoveMedia(questionId, followupIndex)}
                okText="Yes"
                cancelText="No"
                placement="topRight"
              >
                <MediaRemoveBtn className="pdf-remove-btn">
                  <CloseOutlined />
                </MediaRemoveBtn>
              </Popconfirm>
            )}
          </div>
        </PdfDisplayCard>
      );
    }

    return null;
  };

  return (
    <Row gutter={[16, 16]}>
      <PopconfirmGlobalStyles />
      <Col xs={24} sm={24} md={24} lg={8} xl={8}>
        <ChatPanel
          surveyTitle={surveyTitle}
          messages={messages}
          onSendMessage={onSendMessage}
          isSending={isSending}
          demographics={demographics}
          onDemographicsChange={handleDemographicsChange}
          isSavingDemographics={isSavingDemographics}
          onSaveDemographics={handleSaveDemographics}
          onSaveAndRegenerateQuestions={handleSaveAndRegenerate}
          isRegenerating={isRegenerating}
          questions={sortedQuestions}
          onSurveyTitleChange={onSurveyTitleChange}
          isLoading={isLoading}
        />
      </Col>
      <Col xs={24} sm={24} md={24} lg={16} xl={16}>
        <RightPreviewCard
          className="hasHeader"
          title="Question Preview"
          loading={isLoading}
          extra={
            (canExport || canImport) ? (
              <Space size={8}>
                {canExport && sortedQuestions.length > 0 && (
                  <Dropdown menu={{ items: exportMenuItems }} trigger={['click']}>
                    <ThemeFilterBtn icon={<ExportOutlined />}>
                      Export
                    </ThemeFilterBtn>
                  </Dropdown>
                )}
                {canImport && (
                  <>
                    <ThemeFilterBtn icon={<ImportOutlined />} onClick={() => importFileRef.current?.click()}>
                      Import
                    </ThemeFilterBtn>
                    <input
                      ref={importFileRef}
                      type="file"
                      accept=".txt,.csv,.xls,.xlsx"
                      style={{ display: 'none' }}
                      onChange={handleImportFileChange}
                    />
                  </>
                )}
              </Space>
            ) : null
          }
        >
          <RightCardBody>
            <Squares speed={0.2} squareSize={40} direction="diagonal" borderColor="#dbdbdb" hoverFillColor="#dbdbdb">
              <RightScrollArea>
                <QuestionsContainer>
                  {localQuestions.length === 0 ? (
                    <NoQuestionsState>
                        <Lottie animationData={questionGenAnimation} loop autoplay />
                      <NoQuestionsTitle>No Questions Added Yet</NoQuestionsTitle>
                    </NoQuestionsState>
                  ) : isRegenerating && !regeneratingQuestionId ? (
                    <NoQuestionsState>
                        <Lottie animationData={questionGenAnimation} loop autoplay />
                      <NoQuestionsTitle>Regenerating Questions...</NoQuestionsTitle>
                    </NoQuestionsState>
                  ) : (
                    localQuestions.map((question, index) => (
                      <DraggableQuestionCard
                        key={question._id}
                        $isDragging={draggedIndex === index}
                        onDragOver={(e) => handleDragOver(e, index)}
                      >
                        <QuestionCardHeaderWrapper>
                          <QuestionCardHeader>
                            <DragHandle
                              draggable
                              onDragStart={(e) => handleDragStart(e, index)}
                              onDragEnd={handleDragEnd}
                            >
                              <HolderOutlined />
                            </DragHandle>
                            <QuestionBadge>Q{index + 1}</QuestionBadge>
                          </QuestionCardHeader>
                          <QuestionHeaderActions>
                            {onRegenerateQuestion && (
                              <Popconfirm
                                title="Regenerate Question"
                                description={`Are you sure you want to regenerate Q${index + 1}?`}
                                onConfirm={() => handleRegenerateQuestion(question._id)}
                                okText="Yes"
                                cancelText="No"
                                placement="topRight"
                                disabled={isRegenerating || regeneratingQuestionId === question._id}
                              >
                                <Tooltip title="Regenerate question">
                                  <QuestionRegenerateBtn
                                    disabled={isRegenerating || regeneratingQuestionId === question._id}
                                  >
                                    {regeneratingQuestionId === question._id ? (
                                      <SyncOutlined spin />
                                    ) : (
                                      <ReloadOutlined />
                                    )}
                                  </QuestionRegenerateBtn>
                                </Tooltip>
                              </Popconfirm>
                            )}
                            <Tooltip title="Edit question">
                              <ThemeFilterBtn
                                className="btnBorder"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => handleEditQuestion(question)}
                              />
                            </Tooltip>
                            <Popconfirm
                              title="Delete Question"
                              description="Are you sure you want to delete this question?"
                              onConfirm={() => onDeleteQuestion?.(question._id)}
                              okText="Yes"
                              cancelText="No"
                              placement="topRight"
                            >
                              <Tooltip title="Delete question">
                                <ThemeFilterBtn
                                  className="btnBorder"
                                  size="small"
                                  icon={<DeleteOutlined />}
                                />
                              </Tooltip>
                            </Popconfirm>
                            <Tooltip title="Add follow-up">
                              <ThemeFilterBtn
                                className="btnBorder"
                                size="small"
                                icon={<EnterOutlined />}
                                onClick={() => handleAddFollowup(question._id)}
                              />
                            </Tooltip>
                            <Tooltip title={question.media?.media_type ? "Replace media" : "Attach media"}>
                              <ThemeFilterBtn
                                className="btnBorder"
                                size="small"
                                icon={existingMediaUploading === question._id ? <LoadingOutlined spin /> : <FileImageOutlined />}
                                onClick={() => handleAttachMediaToExisting(question._id)}
                                disabled={!!existingMediaUploading}
                              />
                            </Tooltip>
                          </QuestionHeaderActions>
                        </QuestionCardHeaderWrapper>
                        <QuestionCardInner>
                          {editingQuestionId !== question._id && renderMediaDisplay(question.media, question._id)}
                          {editingQuestionId === question._id ? (
                            <QuestionEditWrapper>
                              <input
                                type="text"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                autoFocus
                              />
                              <button className="save" onClick={handleSaveQuestion}>
                                Save
                              </button>
                              <button className="cancel" onClick={handleCancelEdit}>
                                Cancel
                              </button>
                            </QuestionEditWrapper>
                          ) : (
                            <ThemeQstName className="darkColor">{question.question_text}</ThemeQstName>
                          )}
                          {/* Follow-ups */}
                          {question.followups?.length > 0 && (
                            <FollowupContainer>
                              {question.followups.map((followup, fIndex) => {
                                const hasFollowupMedia = followup.media?.media_type;
                                const isEditingFu = editingFollowup?.questionId === question._id && editingFollowup?.followupIndex === fIndex;
                                const followupActions = (
                                  <FollowupActions>
                                    <Tooltip title="Edit follow-up">
                                      <FollowupActionBtn onClick={() => handleEditFollowup(question._id, fIndex, followup.text)}>
                                        <EditOutlined />
                                      </FollowupActionBtn>
                                    </Tooltip>
                                    <Popconfirm
                                      title="Delete Follow-up"
                                      description="Are you sure you want to delete this follow-up?"
                                      onConfirm={() => handleDeleteFollowup(question._id, fIndex)}
                                      okText="Yes"
                                      cancelText="No"
                                      placement="topRight"
                                    >
                                      <Tooltip title="Delete follow-up">
                                        <FollowupActionBtn>
                                          <DeleteOutlined />
                                        </FollowupActionBtn>
                                      </Tooltip>
                                    </Popconfirm>
                                    <Tooltip title="Add follow-up below">
                                      <FollowupActionBtn onClick={() => handleAddFollowup(question._id, fIndex)}>
                                        <PlusOutlined />
                                      </FollowupActionBtn>
                                    </Tooltip>
                                    <Tooltip title={hasFollowupMedia ? "Replace media" : "Attach media"}>
                                      <FollowupActionBtn
                                        onClick={() => handleAttachMediaToExisting(question._id, fIndex)}
                                        disabled={!!existingMediaUploading}
                                      >
                                        {existingMediaUploading === `${question._id}-${fIndex}` ? <LoadingOutlined spin /> : <FileImageOutlined />}
                                      </FollowupActionBtn>
                                    </Tooltip>
                                  </FollowupActions>
                                );
                                return (
                                  <FollowupRow
                                    key={fIndex}
                                    $isDragging={draggedFollowup?.questionId === question._id && draggedFollowup?.index === fIndex}
                                    $hasMedia={!!hasFollowupMedia && !isEditingFu}
                                    onDragOver={(e) => handleFollowupDragOver(e, question._id, fIndex)}
                                  >
                                    {isEditingFu ? (
                                      <>
                                        <FollowupDragHandle
                                          draggable
                                          onDragStart={(e) => handleFollowupDragStart(e, question._id, fIndex)}
                                          onDragEnd={handleFollowupDragEnd}
                                        >
                                          <HolderOutlined />
                                        </FollowupDragHandle>
                                        <FollowupBadge>F{fIndex + 1}</FollowupBadge>
                                        <QuestionEditWrapper style={{ flex: 1 }}>
                                          <input
                                            type="text"
                                            value={editingFollowupValue}
                                            onChange={(e) => setEditingFollowupValue(e.target.value)}
                                            autoFocus
                                          />
                                          <button className="save" onClick={handleSaveFollowup}>Save</button>
                                          <button className="cancel" onClick={handleCancelFollowupEdit}>Cancel</button>
                                        </QuestionEditWrapper>
                                      </>
                                    ) : hasFollowupMedia ? (
                                      <>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: 'calc(100% + 24px)', marginLeft: -12, marginRight: -12, marginTop: -10, padding: '10px 12px', borderBottom: '1px solid rgba(61, 41, 105, 0.1)' }}>
                                          <FollowupDragHandle
                                            draggable
                                            onDragStart={(e) => handleFollowupDragStart(e, question._id, fIndex)}
                                            onDragEnd={handleFollowupDragEnd}
                                          >
                                            <HolderOutlined />
                                          </FollowupDragHandle>
                                          <FollowupBadge>F{fIndex + 1}</FollowupBadge>
                                          <div style={{ marginLeft: 'auto' }}>{followupActions}</div>
                                        </div>
                                        {renderMediaDisplay(followup.media, question._id, fIndex)}
                                        <FollowupText>{followup.text}</FollowupText>
                                      </>
                                    ) : (
                                      <>
                                        <FollowupDragHandle
                                          draggable
                                          onDragStart={(e) => handleFollowupDragStart(e, question._id, fIndex)}
                                          onDragEnd={handleFollowupDragEnd}
                                        >
                                          <HolderOutlined />
                                        </FollowupDragHandle>
                                        <FollowupBadge>F{fIndex + 1}</FollowupBadge>
                                        <FollowupText>{followup.text}</FollowupText>
                                        {followupActions}
                                      </>
                                    )}
                                  </FollowupRow>
                                );
                              })}
                            </FollowupContainer>
                          )}
                        </QuestionCardInner>
                        <InsertButtonWrapper className="insert-btn-wrapper">
                          <InsertAfterButton onClick={(e) => handleInsertAfter(e, index)}>
                            <PlusOutlined />
                            Insert After
                          </InsertAfterButton>
                        </InsertButtonWrapper>
                      </DraggableQuestionCard>
                    ))
                  )}
                </QuestionsContainer>

                {onSaveSurvey && sortedQuestions.length > 0 && !(isRegenerating && !regeneratingQuestionId) && (
                  <SaveButtonWrapper>
                    <ThemeButton
                      className="noBackground"
                      size="large"
                      loading={isSavingSurvey}
                      disabled={isSavingSurvey}
                      onClick={onSaveSurvey}
                    >
                      Save
                    </ThemeButton>
                    <ThemeButton
                      size="large"
                      loading={isSavingSurvey}
                      disabled={isSavingSurvey}
                      onClick={onSaveAndBuildAIPoll}
                    >
                      Save & Design
                    </ThemeButton>
                  </SaveButtonWrapper>
                )}
              </RightScrollArea>
            </Squares>
          </RightCardBody>
        </RightPreviewCard>
      </Col>

      {/* Hidden file input for attaching media to existing questions/followups */}
      <input
        ref={existingMediaInputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.mp4,.pdf"
        style={{ display: 'none' }}
        onChange={handleExistingMediaInputChange}
      />

      {insertModalOpen && (
        <InsertModalOverlay onClick={handleInsertModalClose}>
          <InsertModalContent onClick={(e) => e.stopPropagation()}>
            <InsertModalTitle>Insert New Question</InsertModalTitle>
            <InsertModalInput
              value={insertQuestionText}
              onChange={(e) => setInsertQuestionText(e.target.value)}
              placeholder="Enter your question..."
              autoFocus
            />
            <InsertModalLabel>Attach Media (Optional)</InsertModalLabel>
            {renderMediaUpload(insertMediaData, insertMediaUploading, 'insert')}
            <input
              ref={mediaInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.mp4,.pdf"
              style={{ display: 'none' }}
              onChange={(e) => handleMediaInputChange(e, 'insert')}
            />
            <InsertModalActions>
              <InsertModalButton className="secondary" onClick={handleInsertModalClose}>
                Cancel
              </InsertModalButton>
              <InsertModalButton
                className="primary"
                onClick={handleInsertModalSubmit}
                disabled={!insertQuestionText.trim() || insertMediaUploading}
              >
                Insert Question
              </InsertModalButton>
            </InsertModalActions>
          </InsertModalContent>
        </InsertModalOverlay>
      )}

      {followupModalOpen && (
        <InsertModalOverlay onClick={handleFollowupModalClose}>
          <InsertModalContent onClick={(e) => e.stopPropagation()}>
            <InsertModalTitle>
              {followupInsertAfterIndex !== null ? `Insert Follow-up After F${followupInsertAfterIndex + 1}` : 'Add Follow-up Question'}
            </InsertModalTitle>
            <InsertModalInput
              value={newFollowupText}
              onChange={(e) => setNewFollowupText(e.target.value)}
              placeholder="Enter follow-up question..."
              autoFocus
            />
            <InsertModalLabel>Attach Media (Optional)</InsertModalLabel>
            {renderMediaUpload(followupMediaData, followupMediaUploading, 'followup')}
            <input
              ref={followupMediaInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.mp4,.pdf"
              style={{ display: 'none' }}
              onChange={(e) => handleMediaInputChange(e, 'followup')}
            />
            <InsertModalActions>
              <InsertModalButton className="secondary" onClick={handleFollowupModalClose}>
                Cancel
              </InsertModalButton>
              <InsertModalButton
                className="primary"
                onClick={handleFollowupModalSubmit}
                disabled={!newFollowupText.trim() || followupMediaUploading}
              >
                {followupInsertAfterIndex !== null ? 'Insert Follow-up' : 'Add Follow-up'}
              </InsertModalButton>
            </InsertModalActions>
          </InsertModalContent>
        </InsertModalOverlay>
      )}

      {/* Import Questions Confirmation Modal */}
      <ThemeModal
        title="Import Questions"
        open={importModalVisible}
        onCancel={() => { if (!isImporting) { setImportModalVisible(false); setPendingImport(null); } }}
        onOk={handleImportConfirm}
        confirmLoading={isImporting}
        okText={isImporting ? 'Importing...' : (sortedQuestions.length > 0 ? 'Replace Questions' : 'Import Questions')}
        cancelButtonProps={{ disabled: isImporting }}
        width={480}
        destroyOnClose
      >
        <p>
          {sortedQuestions.length > 0
            ? <>This will replace all existing questions with <strong>{pendingImport?.questions?.length || 0}</strong> question{(pendingImport?.questions?.length || 0) !== 1 ? 's' : ''} from <strong>{pendingImport?.fileName}</strong>.</>
            : <>This will import <strong>{pendingImport?.questions?.length || 0}</strong> question{(pendingImport?.questions?.length || 0) !== 1 ? 's' : ''} from <strong>{pendingImport?.fileName}</strong>.</>
          }
        </p>
      </ThemeModal>

      {/* Media Preview Modal */}
      <ThemeModal
        title="Media Preview"
        open={!!previewMedia}
        onCancel={() => setPreviewMedia(null)}
        footer={null}
        width={previewMedia?.media_type === 'pdf' ? 680 : '70%'}
        centered
        destroyOnClose
      >
        {previewMedia?.media_type === 'image' && (
          <img src={previewMedia.url} alt={previewMedia.filename} style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', display: 'block', margin: '0 auto' }} />
        )}
        {previewMedia?.media_type === 'pdf' && (
          <iframe src={`${previewMedia.url}#toolbar=0&navpanes=0&view=FitH`} style={{ width: '100%', height: '80vh', border: 'none', background: '#fff' }} title={previewMedia.filename} />
        )}
      </ThemeModal>
    </Row>
  );
};

export default QuestionPreview;
